/* tokenTarget.ts */

/**
 * Whether the bearer token may be attached to requests going to a base URL.
 *
 * The API base URLs come from build-time Vite variables, so this guards
 * against a *misconfigured build* shipping credentials to a third-party host —
 * not against anything an attacker can change at runtime. It also only stops
 * the credentials: a build pointed at a foreign host would still send the
 * request bodies there, so this is no substitute for controlling the build.
 *
 * Rules:
 * * The base URL is resolved against the page's origin before being judged,
 *   so a relative prefix — `/api`, `/agent`, which is what every deployment
 *   uses — comes out same-origin, while a protocol-relative one such as
 *   `//elsewhere.example/api` is correctly seen as the foreign host it is.
 * * The resolved origin must match the page's own origin.
 * * A development build may additionally talk to the loopback interface,
 *   because that is how the APIs are run locally (e.g.
 *   `VITE_API_PREFIX=http://localhost:8000/api/v1`).
 */
export const mayReceiveToken = (baseURL: string): boolean => {
  try {
    const { origin, hostname } = new URL(
      baseURL,
      window.location.origin
    );

    if (origin === window.location.origin) {
      return true;
    }

    return (
      Boolean(import.meta.env.DEV) &&
      (hostname === "localhost" || hostname === "127.0.0.1")
    );
  } catch {
    // An unparseable base URL is not a target we can vouch for.
    return false;
  }
};
